import type { CompositionTreeNode } from './propertyCompositionTree';

export type BubbleKind = 'root' | 'aggregate' | 'leaf';

export type LaidOutBubble = {
    id: string;
    node: CompositionTreeNode;
    kind: BubbleKind;
    x: number;
    y: number;
    r: number;
    hasHalo: boolean;
    /** 'inside' for large roots; 'side' for mid/leaf captions */
    labelMode: 'inside' | 'side';
    labelSide: 'left' | 'right';
};

export type LaidOutEdge = {
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
};

export type CompositionBubbleLayout = {
    bubbles: LaidOutBubble[];
    edges: LaidOutEdge[];
    width: number;
    height: number;
};

const ROOT_R = 54;
const AGGREGATE_R = 30;
const LEAF_R = 11;
const LEAF_R_EMPHASIS = 13;
const PAD = 72;
const CLUSTER_GAP = 56;
const LABEL_PAD = 96;

const emphasisBadge = (badges: string[]): boolean => (
    badges.includes('essential')
    || badges.includes('efa')
);

const kindOf = (depth: number, node: CompositionTreeNode): BubbleKind => {
    if (depth === 0) {
        return 'root';
    }
    if (node.children.length > 0 || depth === 1) {
        return 'aggregate';
    }
    return 'leaf';
};

const radiusOf = (kind: BubbleKind, node: CompositionTreeNode): number => {
    if (kind === 'root') {
        return ROOT_R;
    }
    if (kind === 'aggregate') {
        return AGGREGATE_R;
    }
    return emphasisBadge(node.badges) ? LEAF_R_EMPHASIS : LEAF_R;
};

const leafRingRadius = (count: number): number => {
    if (count <= 0) {
        return 0;
    }
    const circumference = count * (LEAF_R * 2 + 10);
    return Math.max(78, circumference / (2 * Math.PI) + 18);
};

const midRingRadius = (mids: CompositionTreeNode[]): number => {
    if (mids.length === 0) {
        return 0;
    }
    const leafBudgets = mids.map((m) => leafRingRadius(m.children.length) + AGGREGATE_R);
    const maxLeaf = Math.max(0, ...leafBudgets);
    const base = Math.max(130, 70 + mids.length * 36);
    return base + maxLeaf * 0.15;
};

type MutablePoint = { id: string; x: number; y: number; r: number };

const layoutCluster = (
    root: CompositionTreeNode,
    originX: number,
    originY: number,
): { bubbles: LaidOutBubble[]; edges: LaidOutEdge[]; bounds: { minX: number; minY: number; maxX: number; maxY: number } } => {
    const bubbles: LaidOutBubble[] = [];
    const edges: LaidOutEdge[] = [];
    const points: MutablePoint[] = [];

    const pushBubble = (
        node: CompositionTreeNode,
        kind: BubbleKind,
        x: number,
        y: number,
        hasHalo: boolean,
    ) => {
        const r = radiusOf(kind, node);
        const labelSide: 'left' | 'right' = x >= originX ? 'right' : 'left';
        bubbles.push({
            id: node.id,
            node,
            kind,
            x,
            y,
            r,
            hasHalo,
            labelMode: kind === 'root' ? 'inside' : 'side',
            labelSide,
        });
        points.push({ id: node.id, x, y, r });
    };

    pushBubble(root, 'root', originX, originY, true);

    const mids = root.children;
    const midRing = midRingRadius(mids);
    const midCount = Math.max(mids.length, 1);

    mids.forEach((mid, index) => {
        const midAngle = -Math.PI / 2 + (2 * Math.PI * index) / midCount;
        const mx = originX + Math.cos(midAngle) * midRing;
        const my = originY + Math.sin(midAngle) * midRing;
        const midKind = kindOf(1, mid);
        pushBubble(mid, midKind, mx, my, midKind === 'aggregate');
        edges.push({
            id: `${root.id}->${mid.id}`,
            x1: originX,
            y1: originY,
            x2: mx,
            y2: my,
        });

        const leaves = mid.children;
        if (leaves.length === 0) {
            return;
        }

        const ring = leafRingRadius(leaves.length);
        const fan = Math.min(Math.PI * 1.35, leaves.length * 0.32);
        const start = midAngle - fan / 2;

        leaves.forEach((leaf, leafIndex) => {
            const t = leaves.length === 1
                ? midAngle
                : start + (fan * leafIndex) / (leaves.length - 1);
            const lx = mx + Math.cos(t) * ring;
            const ly = my + Math.sin(t) * ring;
            pushBubble(leaf, 'leaf', lx, ly, false);
            edges.push({
                id: `${mid.id}->${leaf.id}`,
                x1: mx,
                y1: my,
                x2: lx,
                y2: ly,
            });
        });
    });

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    points.forEach((p) => {
        minX = Math.min(minX, p.x - p.r - LABEL_PAD);
        minY = Math.min(minY, p.y - p.r - 28);
        maxX = Math.max(maxX, p.x + p.r + LABEL_PAD);
        maxY = Math.max(maxY, p.y + p.r + 36);
    });
    if (!Number.isFinite(minX)) {
        minX = originX - ROOT_R;
        minY = originY - ROOT_R;
        maxX = originX + ROOT_R;
        maxY = originY + ROOT_R;
    }

    return { bubbles, edges, bounds: { minX, minY, maxX, maxY } };
};

/** Deterministic radial clusters for protein / fat / carbohydrate roots. */
export const layoutCompositionBubbles = (
    roots: CompositionTreeNode[],
): CompositionBubbleLayout => {
    if (roots.length === 0) {
        return { bubbles: [], edges: [], width: 320, height: 240 };
    }

    const drafted = roots.map((root) => layoutCluster(root, 0, 0));
    const heights = drafted.map((c) => c.bounds.maxY - c.bounds.minY);
    const maxH = Math.max(...heights, ROOT_R * 2);

    let cursorX = PAD;
    const bubbles: LaidOutBubble[] = [];
    const edges: LaidOutEdge[] = [];

    drafted.forEach((cluster) => {
        const w = cluster.bounds.maxX - cluster.bounds.minX;
        const h = cluster.bounds.maxY - cluster.bounds.minY;
        const dx = cursorX - cluster.bounds.minX;
        const dy = PAD + (maxH - h) / 2 - cluster.bounds.minY;

        cluster.bubbles.forEach((b) => {
            bubbles.push({
                ...b,
                x: b.x + dx,
                y: b.y + dy,
                labelSide: (b.x + dx) >= cursorX + w / 2 ? 'right' : 'left',
            });
        });
        cluster.edges.forEach((e) => {
            edges.push({
                ...e,
                x1: e.x1 + dx,
                y1: e.y1 + dy,
                x2: e.x2 + dx,
                y2: e.y2 + dy,
            });
        });

        cursorX += w + CLUSTER_GAP;
    });

    const width = cursorX - CLUSTER_GAP + PAD;
    const height = maxH + PAD * 2;

    return { bubbles, edges, width, height };
};
